import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Plus, Clock,
  Copy, MoreHorizontal, Check, Layers, RefreshCw, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useApp } from '@/context/AppContext';
import { publishApi } from '@/services/api';
import { toast } from 'sonner';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const PlatformColor = { instagram: '#E1306C', tiktok: '#69C9D0', youtube: '#FF0000', x: '#9AA4B2', facebook: '#1877F2' };
const PlatformLabel = { instagram: 'IG', tiktok: 'TK', youtube: 'YT', x: 'X', facebook: 'FB' };

const StatusStyle = {
  published: { bg: 'hsl(var(--success) / 0.12)', color: 'hsl(var(--success))' },
  scheduled: { bg: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' },
  draft: { bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground-muted))' },
  failed: { bg: 'hsl(var(--destructive) / 0.12)', color: 'hsl(var(--destructive))' },
};

function getPostDay(post) {
  if (!post.scheduled_at) return null;
  return new Date(post.scheduled_at).getDate();
}
function getPostMonth(post) {
  if (!post.scheduled_at) return null;
  return new Date(post.scheduled_at).getMonth();
}
function getPostYear(post) {
  if (!post.scheduled_at) return null;
  return new Date(post.scheduled_at).getFullYear();
}
function formatPostTime(post) {
  if (!post.scheduled_at) return '--';
  const d = new Date(post.scheduled_at);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function Publish() {
  const { activeBrand, allBrands } = useApp();
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newPost, setNewPost] = useState({
    title: '', platform: 'instagram', brand_id: '', time: '09:00', type: 'reel'
  });
  const [viewMode, setViewMode] = useState('calendar');

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const today = new Date().getDate();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const loadPosts = useCallback(async () => {
    if (!activeBrand?.id) return;
    setLoading(true);
    try {
      const data = await publishApi.listPosts({ brandId: activeBrand.id });
      setPosts(data);
    } catch (e) {
      console.warn('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.id]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Pre-fill brand_id when activeBrand changes
  useEffect(() => {
    if (activeBrand?.id) {
      setNewPost(p => ({ ...p, brand_id: p.brand_id || activeBrand.id }));
    }
  }, [activeBrand?.id]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleSchedule = async () => {
    if (!newPost.title || !newPost.brand_id) return;
    setSaving(true);
    try {
      const day = selectedDay || today;
      const [h, m] = newPost.time.split(':').map(Number);
      const scheduled = new Date(year, month, day, h, m, 0);
      const post = await publishApi.createPost({
        title: newPost.title,
        brand_id: newPost.brand_id,
        platform: newPost.platform,
        post_type: newPost.type,
        scheduled_at: scheduled.toISOString(),
      });
      setPosts(prev => [...prev, post]);
      setScheduleOpen(false);
      setNewPost({ title: '', platform: 'instagram', brand_id: activeBrand?.id || '', time: '09:00', type: 'reel' });
      toast.success('Post scheduled!');
    } catch (e) {
      toast.error(e.message || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await publishApi.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handlePublishNow = async (postId) => {
    try {
      await publishApi.publishNow(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: 'published' } : p));
      toast.success('Post published!');
    } catch {
      toast.error('Failed to publish');
    }
  };

  const getPostsForDay = (day) =>
    posts.filter(p =>
      getPostDay(p) === day &&
      getPostMonth(p) === month &&
      getPostYear(p) === year
    );

  const getBrandName = (brandId) => allBrands.find(b => b.id === brandId)?.name || '';

  const sortedPosts = [...posts].sort((a, b) =>
    new Date(a.scheduled_at) - new Date(b.scheduled_at)
  );

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={prevMonth} style={{ color: 'hsl(var(--foreground-muted))' }}>
            <ChevronLeft size={16} />
          </Button>
          <h2 className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            {MONTHS[month]} {year}
          </h2>
          <Button variant="ghost" size="sm" onClick={nextMonth} style={{ color: 'hsl(var(--foreground-muted))' }}>
            <ChevronRight size={16} />
          </Button>
          <button onClick={loadPosts} className="p-1 rounded"
            style={{ color: 'hsl(var(--foreground-subtle))' }}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-lg"
            style={{ background: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))' }}>
            <button onClick={() => setViewMode('calendar')}
              className="px-3 py-1 rounded-md text-xs font-medium"
              style={{
                background: viewMode === 'calendar' ? 'hsl(var(--primary))' : 'transparent',
                color: viewMode === 'calendar' ? '#fff' : 'hsl(var(--foreground-muted))'
              }}>
              Calendar
            </button>
            <button onClick={() => setViewMode('queue')}
              className="px-3 py-1 rounded-md text-xs font-medium"
              style={{
                background: viewMode === 'queue' ? 'hsl(var(--primary))' : 'transparent',
                color: viewMode === 'queue' ? '#fff' : 'hsl(var(--foreground-muted))'
              }}>
              Queue
            </button>
          </div>
          <Button size="sm" className="gap-1.5 text-xs" onClick={() => { setSelectedDay(null); setScheduleOpen(true); }}
            style={{ background: 'hsl(var(--primary))', color: '#fff' }}
            data-testid="schedule-post-btn">
            <Plus size={13} /> Schedule Post
          </Button>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="reelia-card overflow-hidden">
          <div className="grid grid-cols-7" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            {DAYS.map(d => (
              <div key={d} className="py-2 text-center text-xs font-medium"
                style={{ color: 'hsl(var(--foreground-subtle))' }}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-24 p-2"
                style={{ borderRight: '1px solid hsl(var(--border-muted))', borderBottom: '1px solid hsl(var(--border-muted))' }} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayPosts = getPostsForDay(day);
              const isToday = day === today && month === currentMonth && year === currentYear;
              return (
                <div key={day}
                  className="min-h-24 p-2 cursor-pointer hover:bg-surface-hover"
                  style={{
                    borderRight: '1px solid hsl(var(--border-muted))',
                    borderBottom: '1px solid hsl(var(--border-muted))',
                    background: isToday ? 'hsl(var(--primary) / 0.04)' : 'transparent',
                  }}
                  onClick={() => { setSelectedDay(day); setScheduleOpen(true); }}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs mb-1"
                    style={isToday
                      ? { background: 'hsl(var(--primary))', color: '#fff' }
                      : { color: 'hsl(var(--foreground-muted))' }}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {dayPosts.slice(0, 2).map(post => (
                      <div key={post.id}
                        className="text-xs px-1.5 py-0.5 rounded truncate"
                        style={{
                          background: `${PlatformColor[post.platform]}18`,
                          color: PlatformColor[post.platform],
                          fontSize: '10px'
                        }}>
                        {PlatformLabel[post.platform]} · {post.title}
                      </div>
                    ))}
                    {dayPosts.length > 2 && (
                      <div className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))', fontSize: '10px' }}>
                        +{dayPosts.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="queue-view">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={18} className="animate-spin mr-2" style={{ color: 'hsl(var(--foreground-subtle))' }} />
              <span className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>Loading posts...</span>
            </div>
          )}
          {!loading && sortedPosts.length === 0 && (
            <div className="reelia-card p-10 text-center">
              <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>No posts scheduled</p>
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                Click "Schedule Post" to add your first post
              </p>
            </div>
          )}
          {sortedPosts.map((post, idx) => (
            <motion.div key={post.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.04 }}
              className="reelia-card p-4 flex items-center gap-4"
              data-testid={`post-item-${idx}`}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `${PlatformColor[post.platform]}18` }}>
                <span className="text-xs font-bold" style={{ color: PlatformColor[post.platform] }}>
                  {PlatformLabel[post.platform]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                  {post.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {getBrandName(post.brand_id)}
                  </span>
                  <span style={{ color: 'hsl(var(--foreground-subtle))' }}>·</span>
                  <Clock size={10} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                  <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {post.scheduled_at
                      ? new Date(post.scheduled_at).toLocaleDateString([], { month: 'short', day: 'numeric' })
                      : '--'} · {formatPostTime(post)}
                  </span>
                </div>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full capitalize"
                style={StatusStyle[post.status] || StatusStyle.draft}>
                {post.status}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    <MoreHorizontal size={15} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {post.status !== 'published' && (
                    <DropdownMenuItem onClick={() => handlePublishNow(post.id)}>
                      <Check size={12} className="mr-2" /> Publish Now
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleDelete(post.id)} className="text-destructive">
                    <Trash2 size={12} className="mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </motion.div>
          ))}
        </div>
      )}

      {/* Schedule Post Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Schedule Post{selectedDay ? ` — ${MONTHS[month]} ${selectedDay}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                Title *
              </label>
              <input className="reelia-input" placeholder="Post title..."
                value={newPost.title}
                onChange={e => setNewPost(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Brand *
                </label>
                <Select value={newPost.brand_id} onValueChange={v => setNewPost(p => ({ ...p, brand_id: v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent>
                    {allBrands.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Platform
                </label>
                <Select value={newPost.platform}
                  onValueChange={v => setNewPost(p => ({ ...p, platform: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['instagram', 'tiktok', 'youtube', 'x', 'facebook'].map(pl => (
                      <SelectItem key={pl} value={pl}>
                        {pl.charAt(0).toUpperCase() + pl.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Type
                </label>
                <Select value={newPost.type} onValueChange={v => setNewPost(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['reel', 'video', 'carousel', 'post'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Time
                </label>
                <input type="time" className="reelia-input h-9"
                  value={newPost.time}
                  onChange={e => setNewPost(p => ({ ...p, time: e.target.value }))} />
              </div>
            </div>
            {!selectedDay && (
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Day of month
                </label>
                <input type="number" className="reelia-input h-9" min={1} max={daysInMonth}
                  placeholder={String(today)}
                  onChange={e => setSelectedDay(Number(e.target.value))} />
              </div>
            )}
            {newPost.platform === 'instagram' && (
              <div className="p-3 rounded-lg"
                style={{ background: 'hsl(var(--primary) / 0.06)', border: '1px solid hsl(var(--primary) / 0.2)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--primary))' }}>
                  Instagram Options
                </p>
                <div className="flex gap-2">
                  {['Normal Reel', 'Trial Reel'].map(opt => (
                    <button key={opt} className="text-xs px-3 py-1 rounded-full"
                      style={{
                        background: 'hsl(var(--surface))',
                        border: '1px solid hsl(var(--border))',
                        color: 'hsl(var(--foreground-muted))'
                      }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button size="sm"
              disabled={!newPost.title || !newPost.brand_id || saving}
              onClick={handleSchedule}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
              data-testid="confirm-schedule-btn">
              {saving
                ? <><RefreshCw size={13} className="animate-spin mr-1" /> Saving...</>
                : 'Schedule Post'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
